import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, ArrowRight, CheckCircle2 } from "lucide-react";
import { DERIV_AFFILIATE_URL } from "@/lib/socialLinks";

const AffiliateSignupDialog = ({ open, onOpenChange }) => {
  const trackFunnel = () => {
    // funnel telemetry (fire-and-forget) before leaving the page
    try {
      const api = process.env.REACT_APP_BACKEND_URL;
      navigator.sendBeacon?.(`${api}/api/track/funnel`, new Blob([JSON.stringify({ event: "affiliate_click" })], { type: "application/json" }));
    } catch { /* never block the redirect */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="affiliate-signup-dialog"
        className="bg-vts-bg border-vts-line text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            Create your Deriv account
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <p className="text-sm text-vts-silver/80 font-body leading-relaxed">
            VTS runs on top of <strong className="text-white">Deriv</strong> —
            a regulated broker network. To start trading, create a free Deriv
            account first, then come back here to use our free bots, strategies
            and trading desk.
          </p>

          {/* Steps */}
          <div className="bg-vts-surface/60 border border-vts-line rounded-md p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
              How it works
            </div>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-vts-blue/20 border border-vts-blue/40 text-vts-blue text-xs font-mono flex items-center justify-center shrink-0 mt-0.5">
                  1
                </span>
                <div>
                  <div className="font-medium text-white">Tap the button below</div>
                  <div className="text-xs text-vts-silver/70 mt-0.5">
                    Opens Deriv signup in a new tab — takes ~2 minutes.
                  </div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-vts-blue/20 border border-vts-blue/40 text-vts-blue text-xs font-mono flex items-center justify-center shrink-0 mt-0.5">
                  2
                </span>
                <div>
                  <div className="font-medium text-white">Verify email & set a password</div>
                  <div className="text-xs text-vts-silver/70 mt-0.5">
                    Deriv will email you a confirmation link.
                  </div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-vts-blue/20 border border-vts-blue/40 text-vts-blue text-xs font-mono flex items-center justify-center shrink-0 mt-0.5">
                  3
                </span>
                <div>
                  <div className="font-medium text-white">Come back to VTS</div>
                  <div className="text-xs text-vts-silver/70 mt-0.5">
                    Tap{" "}
                    <strong className="text-vts-blue">
                      Already have a Deriv account?
                    </strong>{" "}
                    on the VTS homepage and connect with your API token.
                  </div>
                </div>
              </li>
            </ol>
          </div>

          {/* Perks */}
          <div className="grid grid-cols-1 gap-2 text-xs">
            <div className="flex items-center gap-2 text-vts-silver/80">
              <CheckCircle2 className="h-3.5 w-3.5 text-vts-profit shrink-0" />
              <span className="text-white font-semibold">VTS is 100% free — no subscription, no charge, ever</span>
            </div>
            <div className="flex items-center gap-2 text-vts-silver/80">
              <CheckCircle2 className="h-3.5 w-3.5 text-vts-profit shrink-0" />
              <span>Free demo account with $10,000 virtual funds</span>
            </div>
            <div className="flex items-center gap-2 text-vts-silver/80">
              <CheckCircle2 className="h-3.5 w-3.5 text-vts-profit shrink-0" />
              <span>Trade Synthetic Indices 24/7 — no market hours</span>
            </div>
            <div className="flex items-center gap-2 text-vts-silver/80">
              <CheckCircle2 className="h-3.5 w-3.5 text-vts-profit shrink-0" />
              <span>Use VTS bots and strategies once you're back here</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="affiliate-cancel-btn"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-vts-line bg-vts-surface hover:bg-vts-surfaceHover text-white"
            >
              Cancel
            </Button>
            <Button
              data-testid="affiliate-create-account-btn"
              asChild
              onClick={trackFunnel}
              className="flex-[2] bg-vts-blue hover:bg-vts-blueHover text-white"
            >
              {/* real <a href> to the affiliate link — visible on long-press/hover,
                  same-tab (new-tab counts as a popup on iOS Safari and gets blocked) */}
              <a href={DERIV_AFFILIATE_URL}>
                Create Deriv account
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>

          <p className="text-[10px] text-vts-silver/50 text-center font-mono">
            By continuing you'll be redirected to{" "}
            <a data-testid="affiliate-visible-link" href={DERIV_AFFILIATE_URL} onClick={trackFunnel}
              className="text-vts-blue underline underline-offset-2 break-all">
              t.deriv.link
            </a>{" "}
            — our official Deriv partner signup link.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AffiliateSignupDialog;
